import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import config from '../../config';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  MenuItem,
} from '@mui/material';
import { Divider } from '@mui/material';
import {
  Paper,
  Typography,
  TablePagination,
  TextField,
  Box,
  CircularProgress,
  Card,
  Chip,
  InputAdornment,
} from '@mui/material';
const company = JSON.parse(sessionStorage.getItem('companyDetails'));

// One label/value detail line inside a hotel card.
const InfoRow = ({ icon, label, value }) => (
  <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', py: 0.4 }}>
    <span className='material-symbols-rounded' style={{ fontSize: 18, color: '#94a3b8', marginTop: 1 }}>{icon}</span>
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.3 }}>{label}</Typography>
      <Typography sx={{ fontSize: 13, color: '#0f172a', fontWeight: 500, wordBreak: 'break-word' }}>{value || '—'}</Typography>
    </Box>
  </Box>
);

const Stars = ({ n }) => (
  <Box sx={{ display: 'inline-flex', gap: '1px' }}>
    {[1, 2, 3, 4, 5].map((i) => (
      <span key={i} className='material-symbols-rounded'
        style={{ fontSize: 16, color: i <= Number(n || 0) ? '#F59E0B' : 'rgba(255,255,255,0.3)', fontVariationSettings: "'FILL' 1" }}>
        star
      </span>
    ))}
  </Box>
);

// Images are stored as file paths; serve them off the API host (minus /api).
const MEDIA_BASE = (config.API_BASE_URL || '').replace(/\/api$/i, '');
const imgUrl = (p) =>
  !p ? '' : /^https?:/i.test(p) ? p : `${MEDIA_BASE}/${String(p).replace(/^\/+/, '')}`;

// Map a common amenity name to a Material Symbol; fall back to a tick.
const AMENITY_ICON = {
  wifi: 'wifi', 'free wifi': 'wifi', internet: 'wifi',
  pool: 'pool', 'swimming pool': 'pool',
  parking: 'local_parking', 'free parking': 'local_parking',
  restaurant: 'restaurant', dining: 'restaurant',
  gym: 'fitness_center', fitness: 'fitness_center',
  spa: 'spa', bar: 'local_bar',
  ac: 'ac_unit', 'air conditioning': 'ac_unit',
  laundry: 'local_laundry_service',
  'room service': 'room_service', roomservice: 'room_service',
  tv: 'tv', breakfast: 'bakery_dining',
  elevator: 'elevator', lift: 'elevator',
  'power backup': 'bolt', generator: 'bolt',
  reception: 'concierge', '24 hour reception': 'schedule',
  garden: 'yard', 'pet friendly': 'pets',
  cctv: 'videocam', security: 'security',
  'hot water': 'water_drop', geyser: 'water_drop',
};
const amenityIcon = (name) => AMENITY_ICON[String(name || '').toLowerCase().trim()] || 'check_circle';

// A single rich hotel card. Fetches its own amenities / attractions / photos.
function HotelCard({ hotel, onEdit }) {
  const [extras, setExtras] = useState({ amenities: [], attractions: [], images: [] });
  const [preview, setPreview] = useState(null); // lightbox: image index or null
  useEffect(() => {
    const token = sessionStorage.getItem('token');
    fetch(`${config.API_BASE_URL}/hotelextras/${hotel.id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d) setExtras({ amenities: d.amenities || [], attractions: d.attractions || [], images: d.images || [] }); })
      .catch(() => {});
  }, [hotel.id]);

  const imgs = extras.images;

  return (
    <Card elevation={0} sx={{ borderRadius: '16px', border: '1px solid #E7ECF3', boxShadow: '0 1px 3px rgba(15,23,42,0.06)', overflow: 'hidden', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* ── Header (blue gradient theme) ── */}
      <Box sx={{ p: 2, background: 'linear-gradient(135deg,#0F172A 0%,#1A56DB 100%)', color: '#fff' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 19, fontWeight: 800, color: '#fff' }} noWrap>{hotel.hotelName}</Typography>
            <Typography sx={{ fontSize: 12.5, color: 'rgba(255,255,255,0.9)' }} noWrap>{hotel.city}{hotel.state ? `, ${hotel.state}` : ''}</Typography>
          </Box>
          <Chip size='small' label={hotel.activeStatus ? 'Active' : 'Inactive'} sx={{ flexShrink: 0, fontWeight: 700, bgcolor: hotel.activeStatus ? '#16A34A' : '#64748b', color: '#fff' }} />
        </Box>
        <Box sx={{ mt: 1 }}><Stars n={hotel.rating} /></Box>
      </Box>

      {/* ── Photos gallery (click to preview) ── */}
      {imgs.length > 0 && (
        <Box sx={{ p: 1.5, borderBottom: '1px solid #E7ECF3', bgcolor: '#F8FAFC' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mb: 0.8 }}>
            <span className='material-symbols-rounded' style={{ fontSize: 16, color: '#94a3b8' }}>photo_library</span>
            <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.3 }}>Photos ({imgs.length})</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.9, overflowX: 'auto', pb: 0.5 }}>
            {imgs.map((img, idx) => (
              <Box key={idx} onClick={() => setPreview(idx)}
                sx={{ position: 'relative', width: 100, height: 74, borderRadius: '10px', overflow: 'hidden', flexShrink: 0, cursor: 'pointer', border: '1px solid #E7ECF3', bgcolor: '#E2E8F0', '&:hover .zoom': { opacity: 1 } }}>
                <Box component='img' src={imgUrl(img)} alt=''
                  sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  onError={(e) => { e.currentTarget.style.opacity = 0; }} />
                <Box className='zoom' sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', background: 'rgba(15,23,42,0.35)', opacity: 0, transition: 'opacity .15s' }}>
                  <span className='material-symbols-rounded' style={{ color: '#fff', fontSize: 22 }}>zoom_in</span>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* ── Lightbox preview ── */}
      <Dialog open={preview !== null} onClose={() => setPreview(null)} maxWidth='lg'
        PaperProps={{ sx: { bgcolor: '#0b1120', borderRadius: '14px', overflow: 'hidden', m: 1 } }}>
        {preview !== null && (
          <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: { xs: '86vw', sm: 480 }, minHeight: 300 }}>
            <Box component='img' src={imgUrl(imgs[preview])} alt=''
              sx={{ maxWidth: '86vw', maxHeight: '80vh', display: 'block', objectFit: 'contain' }} />
            <Box onClick={() => setPreview(null)} sx={{ position: 'absolute', top: 10, right: 10, width: 38, height: 38, borderRadius: '50%', bgcolor: 'rgba(0,0,0,0.55)', display: 'grid', placeItems: 'center', cursor: 'pointer' }}>
              <span className='material-symbols-rounded' style={{ color: '#fff' }}>close</span>
            </Box>
            {imgs.length > 1 && (
              <>
                <Box onClick={() => setPreview((preview - 1 + imgs.length) % imgs.length)} sx={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', width: 40, height: 40, borderRadius: '50%', bgcolor: 'rgba(0,0,0,0.5)', display: 'grid', placeItems: 'center', cursor: 'pointer' }}>
                  <span className='material-symbols-rounded' style={{ color: '#fff' }}>chevron_left</span>
                </Box>
                <Box onClick={() => setPreview((preview + 1) % imgs.length)} sx={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', width: 40, height: 40, borderRadius: '50%', bgcolor: 'rgba(0,0,0,0.5)', display: 'grid', placeItems: 'center', cursor: 'pointer' }}>
                  <span className='material-symbols-rounded' style={{ color: '#fff' }}>chevron_right</span>
                </Box>
                <Box sx={{ position: 'absolute', bottom: 10, left: '50%', transform: 'translateX(-50%)', px: 1.4, py: 0.3, borderRadius: '20px', bgcolor: 'rgba(0,0,0,0.55)', color: '#fff', fontSize: 12, fontWeight: 600 }}>
                  {preview + 1} / {imgs.length}
                </Box>
              </>
            )}
          </Box>
        )}
      </Dialog>

      {/* ── Body ── */}
      <Box sx={{ p: 2, flex: 1 }}>
        {extras.amenities.length > 0 && (
          <Box sx={{ mb: 1.8 }}>
            <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.3, mb: 0.8 }}>Amenities</Typography>
            <Box sx={{ display: 'flex', gap: 0.7, flexWrap: 'wrap' }}>
              {extras.amenities.map((a, idx) => (
                <Box key={idx} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.4, borderRadius: '8px', bgcolor: '#EFF4FF', border: '1px solid #DBE5FF', color: '#1A56DB', fontSize: 12, fontWeight: 600 }}>
                  <span className='material-symbols-rounded' style={{ fontSize: 16 }}>{amenityIcon(a)}</span>
                  {a}
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {extras.attractions.length > 0 && (
          <Box sx={{ mb: 1.8 }}>
            <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.3, mb: 0.8 }}>Nearby Attractions</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4 }}>
              {extras.attractions.map((a, idx) => (
                <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
                  <span className='material-symbols-rounded' style={{ fontSize: 16, color: '#EA580C' }}>place</span>
                  <Typography sx={{ fontSize: 13, color: '#0f172a' }}>{a}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        <Grid container spacing={1}>
          <Grid item xs={12}><InfoRow icon='location_on' label='Address' value={hotel.address} /></Grid>
          <Grid item xs={12}><InfoRow icon='call' label='Mobile' value={hotel.mobile} /></Grid>
          <Grid item xs={12}><InfoRow icon='mail' label='Email' value={hotel.email} /></Grid>
          <Grid item xs={6}><InfoRow icon='login' label='Check-in' value={hotel.checkInTime} /></Grid>
          <Grid item xs={6}><InfoRow icon='logout' label='Check-out' value={hotel.checkOutTime} /></Grid>
          <Grid item xs={6}><InfoRow icon='stairs' label='Floors' value={hotel.flor} /></Grid>
          <Grid item xs={6}><InfoRow icon='event' label='Created' value={hotel.created ? new Date(hotel.created).toLocaleDateString('en-GB') : '—'} /></Grid>
          <Grid item xs={6}><InfoRow icon='receipt_long' label='GST No.' value={hotel.gstNumber} /></Grid>
          <Grid item xs={6}><InfoRow icon='restaurant' label='Food License' value={hotel.foodLicenseNumber} /></Grid>
        </Grid>
      </Box>

      {/* Footer */}
      <Box sx={{ p: 2, pt: 0 }}>
        <Button fullWidth variant='contained'
          startIcon={<span className='material-symbols-rounded' style={{ fontSize: 18 }}>edit</span>}
          onClick={() => onEdit(hotel)}
          sx={{ bgcolor: '#1A56DB', textTransform: 'none', fontWeight: 700, boxShadow: 'none', '&:hover': { bgcolor: '#1646b0', boxShadow: 'none' } }}>
          Update Hotel
        </Button>
      </Box>
    </Card>
  );
}

export default function HotelList() {
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [searchText, setSearchText] = useState('');
  const rowsPerPage = 5;
  const url = `${config.API_BASE_URL}${config.PRODUCT_LIST_API}`;
  const [open, setOpen] = useState(false);
  const [editHotel, setEditHotel] = useState(null);
  useEffect(() => {
    fetchHotels();
  }, []);
  const fetchHotels = async () => {
    const token = sessionStorage.getItem('token');
    if (!token) return;

    try {
      const response = await fetch(
        `${config.API_BASE_URL}/Home/GetHotels/${encodeURIComponent(company?.id)}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (!response.ok) {
        console.error('Failed to fetch hotels:', response.statusText);
        setLoading(false);
        return;
      }

      const data = await response.json();
      console.log(data);
      setHotels(data);
    } catch (error) {
      console.error('Error fetching hotels:', error);
    }
    setLoading(false);
  };
  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleSearchChange = (event) => {
    setSearchText(event.target.value);
    setPage(0);
  };

  const handleEditClick = (hotel) => {
    setEditHotel({
      hotelId: hotel.id,
      hotelName: hotel.hotelName,
      address: hotel.address,
      mobile: hotel.mobile,
      state: hotel.state,
      city: hotel.city,
      email: hotel.email,
      checkInTime: hotel.checkInTime,
      checkOutTime: hotel.checkOutTime,
      termsAndCondition: hotel.termsAndCondition || '',
      instagramLink: hotel.instagramLink || '',
      websiteLink: hotel.websiteLink || '',
      facebookLink: hotel.facebookLink || '',
      gstNumber: hotel.gstNumber || '',
      foodLicenseNumber: hotel.foodLicenseNumber || '',
      bankname: hotel.bankName || '',
      ifsccode: hotel.ifsc || '',
      accountno: hotel.bankAccountNumber || '',
      bankaddress: hotel.bankAddress || '',
    });
    setOpen(true);
  };
  const handleUpdateHotel = async () => {
    const token = sessionStorage.getItem('token');

    try {
      const res = await fetch(`${config.API_BASE_URL}/Home/UpdateHotel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(editHotel),
      });

      if (!res.ok) {
        toast.error('Update failed');
        return;
      }

      setOpen(false);
      fetchHotels(); // refresh list
    } catch (err) {
      console.error(err);
    }
  };

  // Search by multiple fields dynamically
  const filteredHotels = hotels.filter((hotel) =>
    Object.keys(hotel)
      .filter((key) => key !== 'Id') // exclude Id if you want
      .some((key) =>
        hotel[key]?.toString().toLowerCase().includes(searchText.toLowerCase()),
      ),
  );

  if (loading) {
    return (
      <Box
        display='flex'
        justifyContent='center'
        alignItems='center'
        minHeight='50vh'>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ p: { xs: 2, md: 3 }, background: '#F8FAFC', minHeight: '100%' }}>
        {/* Hero banner — same title-card theme as the rest of the app */}
        <Box
          sx={{
            mb: 3,
            p: { xs: 3, sm: 4 },
            borderRadius: 3,
            background: 'linear-gradient(135deg,#0F172A 0%,#1A56DB 100%)',
            color: '#fff',
            position: 'relative',
            overflow: 'hidden',
          }}>
          <Box sx={{ position: 'absolute', top: -40, right: -40, width: 160, height: 160, borderRadius: '50%', background: 'rgba(255,255,255,0.08)' }} />
          <Box sx={{ position: 'absolute', bottom: -30, right: 90, width: 90, height: 90, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
          <Typography variant='h5' fontWeight={700} sx={{ letterSpacing: '-0.02em', color: '#fff', position: 'relative' }}>
            My Hotels
          </Typography>
          <Typography variant='body2' sx={{ opacity: 0.8, mt: 0.5, color: '#fff', position: 'relative' }}>
            Manage your hotel properties, contact details and policies.
          </Typography>
        </Box>

        {/* Search */}
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <TextField
            size='small'
            placeholder='Search hotels…'
            value={searchText}
            onChange={handleSearchChange}
            sx={{ width: { xs: '100%', sm: 280 }, bgcolor: '#fff', borderRadius: 2 }}
            InputProps={{ startAdornment: <InputAdornment position='start'><span className='material-symbols-rounded' style={{ fontSize: 20, color: '#94a3b8' }}>search</span></InputAdornment> }}
          />
        </Box>

        {/* Hotel cards */}
        {filteredHotels.length === 0 ? (
          <Paper elevation={0} sx={{ p: 6, textAlign: 'center', borderRadius: '14px', border: '1px solid #E7ECF3', color: '#64748b' }}>
            <span className='material-symbols-rounded' style={{ fontSize: 42, color: '#cbd5e1' }}>domain_disabled</span>
            <Typography sx={{ mt: 1 }}>No hotels found.</Typography>
          </Paper>
        ) : (
          <Grid container spacing={2}>
            {filteredHotels
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((hotel) => (
                <Grid item xs={12} md={6} key={hotel.id}>
                  <HotelCard hotel={hotel} onEdit={handleEditClick} />
                </Grid>
              ))}
          </Grid>
        )}

        {filteredHotels.length > rowsPerPage && (
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Paper elevation={0} sx={{ borderRadius: '12px', border: '1px solid #E7ECF3' }}>
              <TablePagination
                rowsPerPageOptions={[rowsPerPage]}
                component='div'
                count={filteredHotels.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                labelRowsPerPage=''
              />
            </Paper>
          </Box>
        )}
      </Box>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth='md'
        fullWidth
        scroll='paper'>
        <DialogTitle sx={{ fontWeight: 600 }}>Update Hotel Details</DialogTitle>

        <DialogContent
          sx={{
            bgcolor: '#f9fafb',
            maxHeight: '75vh',
          }}>
          <Grid container spacing={3}>
            {/* ===== BASIC INFORMATION ===== */}
            <Grid item xs={12}>
              <Paper
                variant='outlined'
                sx={{
                  p: 3,
                  width: '100%',
                }}>
                <Typography fontWeight={600} mb={2}>
                  Basic Information
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label='Hotel Name'
                      value={editHotel?.hotelName || ''}
                      onChange={(e) =>
                        setEditHotel({
                          ...editHotel,
                          hotelName: e.target.value,
                        })
                      }
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label='Mobile'
                      value={editHotel?.mobile || ''}
                      onChange={(e) =>
                        setEditHotel({ ...editHotel, mobile: e.target.value })
                      }
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label='Email'
                      type='email'
                      value={editHotel?.email || ''}
                      onChange={(e) =>
                        setEditHotel({ ...editHotel, email: e.target.value })
                      }
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label='Address'
                      value={editHotel?.address || ''}
                      onChange={(e) =>
                        setEditHotel({ ...editHotel, address: e.target.value })
                      }
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label='City'
                      value={editHotel?.city || ''}
                      onChange={(e) =>
                        setEditHotel({ ...editHotel, city: e.target.value })
                      }
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label='State'
                      value={editHotel?.state || ''}
                      onChange={(e) =>
                        setEditHotel({ ...editHotel, state: e.target.value })
                      }
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label='GstNumber'
                      value={editHotel?.gstNumber || ''}
                      onChange={(e) =>
                        setEditHotel({
                          ...editHotel,
                          gstNumber: e.target.value,
                        })
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label='FoodLicenseNumber'
                      value={editHotel?.foodLicenseNumber || ''}
                      onChange={(e) =>
                        setEditHotel({
                          ...editHotel,
                          foodLicenseNumber: e.target.value,
                        })
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label='Bank Name'
                      value={editHotel?.bankname || ''}
                      onChange={(e) =>
                        setEditHotel({
                          ...editHotel,
                          bankname: e.target.value,
                        })
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label='IFSC Code'
                      value={editHotel?.ifsccode || ''}
                      onChange={(e) =>
                        setEditHotel({
                          ...editHotel,
                          ifsccode: e.target.value,
                        })
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label='Accoount Number'
                      value={editHotel?.accountno || ''}
                      onChange={(e) =>
                        setEditHotel({
                          ...editHotel,
                          accountno: e.target.value,
                        })
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label='Bank Address'
                      value={editHotel?.bankaddress || ''}
                      onChange={(e) =>
                        setEditHotel({
                          ...editHotel,
                          bankaddress: e.target.value,
                        })
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      type='time'
                      label='Check-in'
                      InputLabelProps={{ shrink: true }}
                      value={editHotel?.checkInTime || ''}
                      onChange={(e) =>
                        setEditHotel({
                          ...editHotel,
                          checkInTime: e.target.value,
                        })
                      }
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      type='time'
                      label='Check-out'
                      InputLabelProps={{ shrink: true }}
                      value={editHotel?.checkOutTime || ''}
                      onChange={(e) =>
                        setEditHotel({
                          ...editHotel,
                          checkOutTime: e.target.value,
                        })
                      }
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* ===== TERMS & CONDITIONS (100% FULL WIDTH – FIXED) ===== */}
            <Box sx={{ width: '100%', mt: 3 }}>
              <Paper
                variant='outlined'
                sx={{
                  p: 3,
                  width: '100%',
                  boxSizing: 'border-box',
                }}>
                <Typography fontWeight={600} mb={2}>
                  Terms & Conditions
                </Typography>

                <TextField
                  fullWidth
                  multiline
                  rows={5}
                  placeholder='Enter hotel terms & conditions...'
                  value={editHotel?.termsAndCondition || ''}
                  onChange={(e) =>
                    setEditHotel({
                      ...editHotel,
                      termsAndCondition: e.target.value,
                    })
                  }
                />
              </Paper>
            </Box>

            {/* ===== SOCIAL MEDIA LINKS ===== */}
            <Grid item xs={12}>
              <Paper
                variant='outlined'
                sx={{
                  p: 3,
                  width: '100%',
                }}>
                <Typography fontWeight={600} mb={2}>
                  Social Media Links
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label='Website URL'
                      value={editHotel?.websiteLink || ''}
                      onChange={(e) =>
                        setEditHotel({
                          ...editHotel,
                          websiteLink: e.target.value,
                        })
                      }
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label='Facebook URL'
                      value={editHotel?.facebookLink || ''}
                      onChange={(e) =>
                        setEditHotel({
                          ...editHotel,
                          facebookLink: e.target.value,
                        })
                      }
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label='Instagram URL'
                      value={editHotel?.instagramLink || ''}
                      onChange={(e) =>
                        setEditHotel({
                          ...editHotel,
                          instagramLink: e.target.value,
                        })
                      }
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setOpen(false)} color='inherit'>
            Cancel
          </Button>
          <Button
            variant='contained'
            color='primary'
            onClick={handleUpdateHotel}>
            Update Hotel
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
